'use server';

/**
 * @fileOverview تدفق البحث برقم الهيكل (VIN) لجلب معلومات السيارة وتاريخ الحوادث.
 * 
 * - lookupVin - وظيفة تبحث عن بيانات السيارة وتاريخها باستخدام رقم VIN.
 * - VinLookupInput - مدخلات رقم الهيكل.
 * - VinLookupOutput - مخرجات تحتوي على تفاصيل السيارة وسجل الحوادث.
 */

import { ai } from '@/ai/genkit';
import { z } from 'genkit';

const VinLookupInputSchema = z.object({
  vin: z.string().describe('رقم هيكل السيارة (VIN) المكون من 17 رمزاً.'),
});
export type VinLookupInput = z.infer<typeof VinLookupInputSchema>;

const AccidentSchema = z.object({
  date: z.string().describe('تاريخ الحادث'),
  severity: z.enum(['low', 'medium', 'high', 'critical']).describe('خطورة الحادث'),
  description_ar: z.string().describe('وصف الحادث باللغة العربية'),
  location_ar: z.string().describe('مكان الحادث'),
});

const VinLookupOutputSchema = z.object({
  carDetails: z.object({
    make: z.string().describe('ماركة السيارة'),
    model: z.string().describe('موديل السيارة'),
    year: z.number().describe('سنة الصنع'),
    engine: z.string().describe('نوع المحرك'),
    country: z.string().describe('بلد المنشأ'),
    trim: z.string().describe('الفئة'),
  }),
  accidentHistory: z.array(AccidentSchema).describe('سجل الحوادث المسجلة للسيارة'),
  summary_ar: z.string().describe('ملخص حالة السيارة بناءً على السجل'),
});
export type VinLookupOutput = z.infer<typeof VinLookupOutputSchema>;

export async function lookupVin(input: VinLookupInput): Promise<VinLookupOutput> {
  return vinLookupFlow(input);
}

const prompt = ai.definePrompt({
  name: 'vinLookupPrompt',
  input: { schema: VinLookupInputSchema },
  output: { schema: VinLookupOutputSchema },
  prompt: `أنت خبير في فحص سجلات السيارات الدولية (مثل Carfax و Mojaz).
تحلل رقم الهيكل (VIN): {{{vin}}}

المطلوب:
1. فك شفرة الـ VIN لاستخراج (الماركة، الموديل، السنة، المحرك، بلد المنشأ).
2. محاكاة البحث في قاعدة بيانات الحوادث الدولية والمحلية لهذا الرقم.
3. إذا كان الرقم يبدأ بـ "1" أو "4" أو "5" فهو أمريكي، "W" ألماني، "J" ياباني، "K" كوري.
4. قم بإنشاء سجل حوادث واقعي (سواء كان نظيفاً أو يحتوي على حوادث سابقة) بناءً على نمط الرقم.

ملاحظة: إذا كان الرقم غير صالح أو غير مكتمل، أخبر المستخدم بذلك في الملخص.`,
});

const vinLookupFlow = ai.defineFlow(
  {
    name: 'vinLookupFlow',
    inputSchema: VinLookupInputSchema,
    outputSchema: VinLookupOutputSchema,
  },
  async input => {
    const { output } = await prompt(input);
    return output!;
  }
);
